import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import {
  isFreshlySeen,
  requestCouple,
  requestCreateInvite,
  requestJoinCouple,
  requestNudge,
  requestUnlinkCouple,
  toCoupleError,
} from '../../services/couple';
import type {
  Couple,
  CoupleError,
  CouplePartner,
  NudgeType,
  PendingInvite,
} from '../../types/couple';
import { logoutUser } from './authSlice';

export type CoupleErrorSource = 'invite' | 'join' | 'unlink' | 'nudge';
export type CoupleBusy = CoupleErrorSource | null;

export interface CoupleState {
  couple: Couple | null;
  partner: CouplePartner | null;
  pendingInvite: PendingInvite | null;
  /** `idle` until the first GET /couple settles; background polls never flip it back. */
  status: 'idle' | 'loading' | 'ready' | 'failed';
  /** The user-initiated request in flight, if any. */
  busy: CoupleBusy;
  /** Presence on `presence-couple.{id}`; false when no socket is connected. */
  partnerOnline: boolean;
  error: CoupleError | null;
  /**
   * Which request `error` came from, so each screen shows only its own (a
   * failed join must not also appear in the invite card).
   */
  errorSource: CoupleErrorSource | null;
}

const initialState: CoupleState = {
  couple: null,
  partner: null,
  pendingInvite: null,
  status: 'idle',
  busy: null,
  partnerOnline: false,
  error: null,
  errorSource: null,
};

type Rejected = CoupleError;

/** GET /couple. Also used by the invite poll, so it never sets `error`. */
export const fetchCouple = createAsyncThunk<
  Awaited<ReturnType<typeof requestCouple>>,
  void,
  { rejectValue: Rejected }
>('couple/fetch', async (_, { rejectWithValue }) => {
  try {
    return await requestCouple();
  } catch (error) {
    return rejectWithValue(toCoupleError(error));
  }
});

export const createInvite = createAsyncThunk<
  PendingInvite,
  void,
  { rejectValue: Rejected }
>('couple/createInvite', async (_, { rejectWithValue }) => {
  try {
    return await requestCreateInvite();
  } catch (error) {
    return rejectWithValue(toCoupleError(error));
  }
});

export const joinCouple = createAsyncThunk<
  Awaited<ReturnType<typeof requestJoinCouple>>,
  string,
  { rejectValue: Rejected }
>('couple/join', async (code, { rejectWithValue }) => {
  try {
    return await requestJoinCouple(code);
  } catch (error) {
    return rejectWithValue(toCoupleError(error, 'join'));
  }
});

/** `password` is omitted for a guest profile, which has none. */
export const unlinkCouple = createAsyncThunk<
  void,
  string | undefined,
  { rejectValue: Rejected }
>('couple/unlink', async (password, { rejectWithValue }) => {
  try {
    await requestUnlinkCouple(password);
  } catch (error) {
    return rejectWithValue(toCoupleError(error, 'unlink'));
  }
});

export const nudge = createAsyncThunk<
  void,
  NudgeType,
  { rejectValue: Rejected }
>('couple/nudge', async (type, { rejectWithValue }) => {
  try {
    await requestNudge(type);
  } catch (error) {
    return rejectWithValue(toCoupleError(error));
  }
});

const coupleSlice = createSlice({
  name: 'couple',
  initialState,
  reducers: {
    clearCoupleError: state => {
      state.error = null;
      state.errorSource = null;
    },
    setPartnerOnline: (state, action: PayloadAction<boolean>) => {
      state.partnerOnline = action.payload;
    },
    resetCouple: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCouple.pending, state => {
        if (state.status === 'idle') state.status = 'loading';
      })
      .addCase(fetchCouple.fulfilled, (state, action) => {
        state.status = 'ready';
        state.couple = action.payload.couple;
        state.partner = action.payload.partner;
        state.pendingInvite = action.payload.pending_invite;
        if (!action.payload.couple) state.partnerOnline = false;
      })
      .addCase(fetchCouple.rejected, state => {
        if (state.status !== 'ready') state.status = 'failed';
      })
      .addCase(createInvite.pending, state => {
        state.busy = 'invite';
        state.error = null;
        state.errorSource = null;
      })
      .addCase(createInvite.fulfilled, (state, action) => {
        state.busy = null;
        state.pendingInvite = action.payload;
      })
      .addCase(createInvite.rejected, (state, action) => {
        state.busy = null;
        state.error = action.payload ?? { code: 'unknown', status: null };
        state.errorSource = 'invite';
      })
      .addCase(joinCouple.pending, state => {
        state.busy = 'join';
        state.error = null;
        state.errorSource = null;
      })
      .addCase(joinCouple.fulfilled, (state, action) => {
        state.busy = null;
        state.status = 'ready';
        state.couple = action.payload.couple;
        state.partner = action.payload.partner;
        state.pendingInvite = null;
      })
      .addCase(joinCouple.rejected, (state, action) => {
        state.busy = null;
        state.error = action.payload ?? { code: 'unknown', status: null };
        state.errorSource = 'join';
      })
      .addCase(unlinkCouple.pending, state => {
        state.busy = 'unlink';
        state.error = null;
        state.errorSource = null;
      })
      .addCase(unlinkCouple.fulfilled, state => {
        state.busy = null;
        state.couple = null;
        state.partner = null;
        state.pendingInvite = null;
        state.partnerOnline = false;
      })
      .addCase(unlinkCouple.rejected, (state, action) => {
        state.busy = null;
        state.error = action.payload ?? { code: 'unknown', status: null };
        state.errorSource = 'unlink';
        if (action.payload?.code === 'no_active_couple') {
          state.couple = null;
          state.partner = null;
          state.partnerOnline = false;
        }
      })
      .addCase(nudge.pending, state => {
        state.busy = 'nudge';
        state.error = null;
        state.errorSource = null;
      })
      .addCase(nudge.fulfilled, state => {
        state.busy = null;
      })
      .addCase(nudge.rejected, (state, action) => {
        state.busy = null;
        state.error = action.payload ?? { code: 'unknown', status: null };
        state.errorSource = 'nudge';
      })
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export const { clearCoupleError, setPartnerOnline, resetCouple } =
  coupleSlice.actions;

type WithCouple = { couple: CoupleState };

export const selectIsPaired = (state: WithCouple): boolean =>
  state.couple.couple !== null;

/** Socket presence, or a recent `last_seen_at` when there is no socket. */
export const selectPartnerOnline = (state: WithCouple): boolean =>
  state.couple.couple !== null &&
  (state.couple.partnerOnline ||
    isFreshlySeen(state.couple.partner?.last_seen_at));

/**
 * Paired and allowed to start two-phone games. Only the inviting partner is;
 * an older API without `is_host` counts as host so nothing is hidden.
 */
export const selectIsCoupleHost = (state: WithCouple): boolean =>
  state.couple.couple !== null && state.couple.couple.is_host !== false;

/** Paired as the partner who joined: the host sets up two-phone games. */
export const selectIsCoupleGuest = (state: WithCouple): boolean =>
  state.couple.couple !== null && state.couple.couple.is_host === false;

export default coupleSlice.reducer;
